import React from "react";
import lodashGet from "lodash/get";
import moment from "moment";
import { useSelector } from "react-redux";

//material ui
import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";

//charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";
//icons
import ExcelIcon from "mdi-material-ui/FileExcel";
import ArrowBack from "@material-ui/icons/ArrowBack";
//jazasoft
import { PageFooter, Button } from "jazasoft";
//local
import Report from "./Report";
import CardHeader from "../../../components/CardHeader";
import Table from "../../../components/Table";
//helpers
import { getEchartOptions, DataType } from "../../../helpers/reportHelper";
import { colorTomatoRed } from "../../../utils/colors";
import { MacroReportId } from "../../../constants";
import { toFixedSigned } from "../../../utils/helpers";
import { useDeepMemo, useMutation, useQuery } from "../../../hooks";

const useStyles = makeStyles({
  root: {
    margin: "1.5em 0"
  },
  card: {
    margin: "1em 0"
  },
  cardContent: {
    padding: 0,
    overflowX: "auto"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  nowrapTable: {
    whiteSpace: "nowrap"
  }
});

const RollVariationReportView = ({ filter, orderList, history }) => {
  const classes = useStyles();
  const [download] = useMutation({ rawResponse: true });
  const i18nMessages = useSelector(state => state.i18n?.messages || {});

  const { data: settings = [] } = useQuery({ url: `settings`, params: { search: `groupId=='report_alert'` } });

  const onDownload = () => {
    const fileName = "ROLLVARIATION.xlsx";
    const orders = orderList.filter(o => {
      let result = true;
      if (filter.start && filter.end) {
        result = o.orderCloseDate >= moment(filter.start).startOf("day").valueOf() && o.orderCloseDate <= moment(filter.end).endOf("day").valueOf();
      }
      if (result && filter.customerList && filter.customerList.length > 0) {
        result = filter.customerList.map(({ name }) => name).includes(o.customer);
      }
      if (result && filter.season) {
        result = filter.season === o.season;
      }
      if (result && filter.line) {
        result = filter.line === o.line;
      }
      if (result && filter.productList && filter.productList.length > 0) {
        result = filter.productList.map(({ name }) => name).includes(o.product);
      }
      if (result && filter.supplierList && filter.supplierList.length > 0) {
        result = filter.supplierList.map(({ name }) => name).includes(o.supplier);
      }
      if (result && filter.fabricPatternList && filter.fabricPatternList.length > 0) {
        result = filter.fabricPatternList.map(({ name }) => name).includes(o.fabricPattern);
      }
      if (result && filter.fabricCategoryList && filter.fabricCategoryList.length > 0) {
        result = filter.fabricCategoryList.map(({ name }) => name).includes(o.fabricCategory);
      }
      return result;
    });

    const columns = [
      { dataKey: "orderCloseDate", title: "Close Date", type: "date" },
      // { dataKey: "cuttingCompletedDate", title: "Cut Date", type: "date" },
      // { dataKey: "line", title: "Line/Floor/Factory" },
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "customer", title: "Customer" },
      { dataKey: "season", title: "Season" },
      { dataKey: "style", title: "Style" },
      { dataKey: "styleColor", title: "Style Color" },
      { dataKey: "styleNo", title: "Style No" },
      { dataKey: "part", title: "Part" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "cutQty", title: "Cut Qty" },
      { dataKey: "rejectedQty", title: "Rejected Qty" },
      { dataKey: "itemCode", title: "FAB Item Code" },
      { dataKey: "supplier", title: "Mill" },
      { dataKey: "fabricPattern", title: "Fab. Pattern" },
      { dataKey: "fabricCategory", title: "Fab. Category" },
      { dataKey: "fabUsed", title: "Fabric Used" },
      { dataKey: "avgRollLength", title: "Average Roll Length" },
      { dataKey: "splitCount", title: "No of Splits" },
      { dataKey: "width", title: "Fab. BOM Width (IN CM)" },
      { dataKey: "actualWidth", title: "Fab. Acutal Width (IN CM)" },
      { dataKey: "widthDiff", title: "Width Difference (IN CM)" }
      // { dataKey: "rollWidthDeviation", title: "Width Deviation Range" }
    ].map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));

    const rowData = orders
      .map(r => {
        const avgRollLength = Math.round(r.available / (r.rollCount - r.splitCount));
        return {
          ...r,
          fabUsed: r.available,
          avgRollLength,
          widthDiff: toFixedSigned(r.actualWidth - r.width)
        };
      })
      .map((row, idx) => ({ rowNo: idx + 1, cellList: columns.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] })) }));

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [
        {
          sheetNo: 0,
          sheetName: "ROLL DEVIATION REPORT",
          autoSizeColumn: true,
          excelData: { columnList: columns.map((c, idx) => ({ ...c, colNo: idx + 1 })), rowList: rowData }
        }
      ],
      params: { filename: fileName }
    };
    download(options, {
      onSuccess: response => {
        const header = response.headers["content-disposition"];
        var filename = header ? header.match(/filename="(.+)"/)[1] : "ROLLVARIATION.xlsx";

        let blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        let link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      }
    });
  };

  const widthVariationOptions = useDeepMemo(() => {
    return getEchartOptions({
      orderList,
      dataTypeList: DataType.WIDTH_DEVIATION,
      settings
    })[DataType.WIDTH_DEVIATION];
  }, [orderList, settings]);

  const lengthVariationOptions = useDeepMemo(() => {
    return getEchartOptions({
      orderList,
      dataTypeList: DataType.LENGTH_VARIATION
    })[DataType.LENGTH_VARIATION];
  }, [orderList]);

  return (
    <div className={classes.root}>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardHeader title="Roll Width Deviation" />
              <Divider />
              <CardContent>
                <div>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={widthVariationOptions && widthVariationOptions.chartOptions}
                    style={{ height: "235px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </div>
                <Paper style={{ marginTop: "1.5em", height: 375, overflowY: "auto", overflowX: "auto" }}>
                  <Table
                    classes={{ table: classes.nowrapTable }}
                    columns={widthVariationOptions.columns}
                    rows={widthVariationOptions.rows}
                    cellStyle={({ column }) =>
                      column.dataKey === "widthDeviation"
                        ? {
                            color: colorTomatoRed.main
                          }
                        : {}
                    }
                    i18nKey="resources.report.arr.excelColumns"
                  />
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <CardHeader title="Roll Length Variation" />
              <Divider />
              <CardContent>
                <div>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={lengthVariationOptions && lengthVariationOptions.chartOptions}
                    style={{ height: "235px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </div>
                <Paper style={{ marginTop: "1.5em", height: 375, overflowY: "auto", overflowX: "auto" }}>
                  <Table
                    classes={{ table: classes.nowrapTable }}
                    columns={lengthVariationOptions.columns}
                    rows={lengthVariationOptions.rows}
                    cellStyle={({ column }) =>
                      column.dataKey === "avgRollLength"
                        ? {
                            color: colorTomatoRed.main
                          }
                        : {}
                    }
                    i18nKey="resources.report.arr.excelColumns"
                  />
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>

      <PageFooter className={classes.displayCenter}>
        <Button label="Back" variant="contained" onClick={() => history.goBack()}>
          <ArrowBack />
        </Button>
        <Button label="Download" variant="contained" style={{ color: "green", marginLeft: "1.5em" }} onClick={onDownload}>
          <ExcelIcon />
        </Button>
      </PageFooter>
    </div>
  );
};

const WastageReport = props => (
  <Report title="Roll Variation Report" defaultMonths={3} macroReportId={MacroReportId.ROLL_VARIATION} {...props}>
    <RollVariationReportView {...props} />
  </Report>
);

export default WastageReport;
